import React from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { Banner, EmptyState, LoadingIndicator, Text } from "@jsq/ds/core";
import { useFayAuthGateArmed } from "#hooks/useFayAuthGate";
import { useFayPackageStaging } from "#hooks/useFayPackageStaging";
import { NEVER_PUBLISHED_LABEL, PUBLISH_STATE_LABEL } from "#utils/publishState";
import { MarkdownContent } from "../MarkdownContent";
import * as styles from "./styles_vanilla.css";
import type { PackageState } from "#graphql/supergraphTypes";
import type { PackageEnumerationStatus, PackageExecutiveSummaryStatus } from "#viewModelTypes";
import type { ApolloError } from "@apollo/client";

export type SummaryTabProps = {
  /** The cached executive summary, or null when none has been generated yet. */
  summary: { text: string; generatedAt: string; isStale: boolean } | null;
  /** Client-visibility state of the package, and when it last changed. Null
   *  when no publish event exists, which reads as "never published". */
  publishState: PackageState | null;
  publishStateChangedAt: string | null;
  /** Whether the fund admin manages this package's entity. Publish state is
   *  meaningless otherwise, so the footer renders nothing when false. Optional,
   *  and absent means false: this type is a published export, so a required
   *  prop would break every consumer rendering the tab directly. */
  isFaManaged?: boolean;
  /** Package enumeration lifecycle; the fallback signal when `summaryStatus` is
   *  absent (older supergraph without the field published). */
  enumerationStatus: PackageEnumerationStatus | null;
  /** Summary lifecycle: the authoritative signal for the null-summary render —
   *  busy while PENDING/GENERATING, error only on FAILED. */
  summaryStatus: PackageExecutiveSummaryStatus | null;
  loading: boolean;
  error?: ApolloError;
};

/** Last publish transition, under the summary. Renders on the settled states
 *  only — a timestamp beside "Generating summary…" would date the wrong thing.
 *
 *  A null timestamp is "no transition yet" rather than a missing value, so it
 *  gets its own copy instead of a fabricated date. */
const PublishFooter: React.FC<{
  publishState: PackageState | null;
  publishStateChangedAt: string | null;
  isFaManaged: boolean;
}> = ({ publishState, publishStateChangedAt, isFaManaged }) => {
  const fayPackageStaging = useFayPackageStaging();
  // Nothing to publish on an entity the fund admin does not manage, so a
  // transition line would date a state that does not apply to the package.
  if (!fayPackageStaging || !isFaManaged) {
    return null;
  }
  const label =
    publishStateChangedAt === null || publishState === null
      ? NEVER_PUBLISHED_LABEL
      : // A publish state this build doesn't know reads as itself, the same
        // fallback the header tag takes.
        `${PUBLISH_STATE_LABEL[publishState] ?? publishState} · ${formatDistanceToNowStrict(
          new Date(publishStateChangedAt),
          { addSuffix: true },
        )}`;
  return (
    <Text variant="secondary-sm" asSpan>
      {label}
    </Text>
  );
};

const InlineStatus: React.FC<{ label: string }> = ({ label }) => (
  <div className={styles.statusRow}>
    <LoadingIndicator size="small" />
    <Text variant="secondary-md" asSpan>
      {label}
    </Text>
  </div>
);

// summaryStatus is the authoritative signal for the null-summary render: PENDING
// = checks still running; GENERATING = checks done, prose being composed (the
// ~15-20s coalesce + Bedrock window). Both are busy states — polling swaps in
// the summary when ready. Falls back to the enumeration heuristic when
// summaryStatus is absent (older supergraph without the field published).
// Returns null (render whatever summary/fallback is present) on FAILED.
const busyStateFor = (
  summaryStatus: PackageExecutiveSummaryStatus | null,
  enumerationStatus: PackageEnumerationStatus | null,
): React.ReactElement | null => {
  if (summaryStatus === "PENDING") {
    return <InlineStatus label="Summary available once checks finish…" />;
  }
  if (summaryStatus === "GENERATING") {
    return <InlineStatus label="Generating summary…" />;
  }
  if (summaryStatus === null && enumerationStatus === "RUNNING") {
    return <InlineStatus label="Summary available once checks finish…" />;
  }
  return null;
};

/**
 * SummaryTab — the default landing tab on the package-detail slideout. Renders
 * the cached, package-level AI executive summary (`package.executiveSummary`) as
 * markdown prose plus a "Generated at last check run" snapshot line. The summary
 * is cached at check-completion and not regenerated on triage, so the copy is
 * snapshot-safe. Null summary is status-aware: still generating vs. absent.
 */
export const SummaryTab: React.FC<SummaryTabProps> = ({
  summary,
  publishState,
  publishStateChangedAt,
  isFaManaged = false,
  enumerationStatus,
  summaryStatus,
  loading,
  error,
}) => {
  // Fay MVP August release gate, read through the auth-gate context so this tab
  // doesn't require a FlagProvider when rendered on its own. Off: reproduce the
  // prior behavior below unchanged (summary renders whenever present, regardless
  // of staleness). On: the fixed behavior further down applies.
  const fayMVPAugust = useFayAuthGateArmed();

  if (error) {
    return (
      <Banner variant="danger" title="Couldn't load summary" description={error.message} />
    );
  }

  if (loading && !summary) {
    return <InlineStatus label="Loading…" />;
  }

  if (fayMVPAugust) {
    // Checked BEFORE `summary` (unlike the legacy check further below): a
    // document added/replaced (or a dependency retrigger) on a package that
    // already has a summary starts a new recheck, but fay keeps returning the
    // PRIOR run's summary until the new one lands — so a present `summary` does
    // not by itself mean it's current. This is the fix; with the gate off, a
    // stale `summary` still wins below, exactly as before.
    const busy = busyStateFor(summaryStatus, enumerationStatus);
    if (busy) {
      return busy;
    }
  }

  if (summary) {
    return (
      <div className={styles.container}>
        <MarkdownContent content={summary.text} richFormatting />
        <Text variant="secondary-sm" asSpan>
          {`Generated at last check run · ${formatDistanceToNowStrict(
            new Date(summary.generatedAt),
            {
              addSuffix: true,
            },
          )}`}
        </Text>
        <PublishFooter
          publishState={publishState}
          publishStateChangedAt={publishStateChangedAt}
          isFaManaged={isFaManaged}
        />
      </div>
    );
  }

  // summary is null here regardless of the gate above — unaffected by the
  // recheck-staleness fix, so this legacy check always runs (first-time package
  // creation relies on it for the pre-checks-complete busy state).
  const busy = busyStateFor(summaryStatus, enumerationStatus);
  if (busy) {
    return busy;
  }

  return (
    <div className={styles.container}>
      <EmptyState
        illustration="searching"
        title="Summary unavailable"
        description="Summary unavailable — see the Checks tab."
      />
      <PublishFooter
        publishState={publishState}
        publishStateChangedAt={publishStateChangedAt}
        isFaManaged={isFaManaged}
      />
    </div>
  );
};
